import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Filter,
  Clock,
  FileText,
  GraduationCap,
  Zap,
  AlertTriangle,
  RefreshCw,
  Settings,
  CheckCircle,
  XCircle,
  Info
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { api } from '../utils/api';
import { formatSafeRelativeTime } from '../utils/date-formatter';
import { PreFilterStats } from '../components/PreFilterStats';

interface FilteredPost {
  id: string;
  username: string;
  content: string;
  content_type: string;
  temporal_context: string;
  prefilter_confidence: number;
  prefilter_reason: string;
  filtered_at: string;
  prefilter_time_ms: number;
  created_at: string;
}

interface PreFilterConfig {
  enabled: boolean;
  mode: 'STRICT' | 'BALANCED' | 'PERMISSIVE';
  thresholds: {
    newsContentPastTenseRatio: number;
    operationalRelevanceMinScore: number;
    autoRejectBelowScore: number;
    requireClassificationBelowScore: number;
  };
}

export default function PreFilterMonitor() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [filteredPosts, setFilteredPosts] = useState<FilteredPost[]>([]);
  const [selectedTimeRange, setSelectedTimeRange] = useState('24h');
  const [selectedContentType, setSelectedContentType] = useState('all');
  const [config, setConfig] = useState<PreFilterConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchData();
  }, [selectedTimeRange, selectedContentType]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch filtered posts
      const postsResponse = await api.get('/api/prefilter/filtered-posts', {
        params: {
          timeRange: selectedTimeRange,
          contentType: selectedContentType
        }
      });
      setFilteredPosts(postsResponse.data || []);

      // Fetch configuration
      const configResponse = await api.get('/api/prefilter/config');
      setConfig(configResponse.data);
    } catch (error) {
      console.error('Error fetching pre-filter data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const getContentTypeIcon = (type: string) => {
    switch (type) {
      case 'news': return <FileText className="h-4 w-4" />;
      case 'educational': return <GraduationCap className="h-4 w-4" />;
      case 'operational': return <Zap className="h-4 w-4" />;
      default: return <AlertTriangle className="h-4 w-4" />;
    }
  };

  const getContentTypeBadgeVariant = (type: string) => {
    switch (type) {
      case 'news': return 'secondary';
      case 'educational': return 'outline';
      case 'operational': return 'default';
      default: return 'destructive';
    }
  };

  const getTemporalBadgeVariant = (context: string) => {
    switch (context) {
      case 'historical': return 'secondary';
      case 'current': return 'default';
      case 'future': return 'outline';
      default: return 'secondary';
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'text-green-600';
    if (confidence >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Filter className="h-6 w-6" />
              Pre-filter Monitor
            </h1>
            <p className="text-muted-foreground">
              Monitor and configure the pre-filter system
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/settings')}
          >
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </Button>
        </div>
      </div>

      {/* Configuration Status */}
      {config && (
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center justify-between">
              <span>Pre-filter Configuration</span>
              <Badge variant={config.enabled ? 'default' : 'secondary'}>
                {config.enabled ? 'Enabled' : 'Disabled'}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Mode</p>
                <p className="text-lg font-semibold">{config.mode}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">News Detection</p>
                <p className="text-lg font-semibold">
                  {(config.thresholds.newsContentPastTenseRatio * 100).toFixed(0)}%
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Min Relevance</p>
                <p className="text-lg font-semibold">
                  {(config.thresholds.operationalRelevanceMinScore * 100).toFixed(0)}%
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Auto Reject</p>
                <p className="text-lg font-semibold">
                  &lt; {(config.thresholds.autoRejectBelowScore * 100).toFixed(0)}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="filtered">Filtered Posts</TabsTrigger>
          <TabsTrigger value="analysis">Analysis</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <PreFilterStats />
        </TabsContent>

        {/* Filtered Posts Tab */}
        <TabsContent value="filtered" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-4">
            <Select value={selectedTimeRange} onValueChange={setSelectedTimeRange}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Time range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1h">Last Hour</SelectItem>
                <SelectItem value="24h">Last 24 Hours</SelectItem>
                <SelectItem value="7d">Last 7 Days</SelectItem>
                <SelectItem value="30d">Last 30 Days</SelectItem>
              </SelectContent>
            </Select>

            <Select value={selectedContentType} onValueChange={setSelectedContentType}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Content type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="news">News</SelectItem>
                <SelectItem value="educational">Educational</SelectItem>
                <SelectItem value="operational">Operational</SelectItem>
                <SelectItem value="unknown">Unknown</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Filtered Posts List */}
          <div className="space-y-4">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
              </div>
            ) : filteredPosts.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <Filter className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-lg font-medium">No filtered posts found</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Try adjusting the time range or content type filter
                  </p>
                </CardContent>
              </Card>
            ) : (
              filteredPosts.map((post) => (
                <Card key={post.id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <CardTitle className="text-base">
                            @{post.username}
                          </CardTitle>
                          <Badge variant={getContentTypeBadgeVariant(post.content_type)}>
                            <span className="flex items-center gap-1">
                              {getContentTypeIcon(post.content_type)}
                              {post.content_type}
                            </span>
                          </Badge>
                          <Badge variant={getTemporalBadgeVariant(post.temporal_context)}>
                            {post.temporal_context}
                          </Badge>
                        </div>
                        <CardDescription>
                          Filtered {formatSafeRelativeTime(post.filtered_at, { addSuffix: true })}
                        </CardDescription>
                      </div>
                      <div className="text-right">
                        <div className={`text-lg font-semibold ${getConfidenceColor(post.prefilter_confidence)}`}>
                          {(post.prefilter_confidence * 100).toFixed(0)}%
                        </div>
                        <p className="text-xs text-muted-foreground">confidence</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm mb-3 line-clamp-3">{post.content}</p>
                    
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-4">
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          {post.prefilter_time_ms}ms
                        </span>
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <Info className="h-3 w-3" />
                          {post.prefilter_reason}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/posts/${post.id}`)}
                      >
                        View Post
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Analysis Tab */}
        <TabsContent value="analysis" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Filter Effectiveness Analysis</CardTitle>
              <CardDescription>
                Detailed analysis of pre-filter performance and accuracy
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Effectiveness Metrics */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Effectiveness Metrics</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">True Positive Rate</span>
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    </div>
                    <p className="text-2xl font-bold">94.3%</p>
                    <p className="text-xs text-muted-foreground">
                      Correctly filtered non-operational content
                    </p>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">False Negative Rate</span>
                      <XCircle className="h-4 w-4 text-red-500" />
                    </div>
                    <p className="text-2xl font-bold">0.8%</p>
                    <p className="text-xs text-muted-foreground">
                      Operational content incorrectly filtered
                    </p>
                  </div>
                </div>
              </div>

              {/* Common Filter Patterns */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Common Filter Patterns</h3>
                <div className="space-y-2">
                  {[
                    { pattern: 'News reporting language', count: 1234, percentage: 45 },
                    { pattern: 'Historical timeframes', count: 876, percentage: 32 },
                    { pattern: 'Educational content', count: 543, percentage: 20 },
                    { pattern: 'Low relevance score', count: 82, percentage: 3 }
                  ].map((item) => (
                    <div key={item.pattern} className="flex items-center justify-between p-3 border rounded">
                      <span className="text-sm font-medium">{item.pattern}</span>
                      <div className="flex items-center gap-3">
                        <Badge variant="secondary">{item.count}</Badge>
                        <span className="text-sm text-muted-foreground">{item.percentage}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recommendations */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Optimization Recommendations</h3>
                <div className="space-y-2">
                  <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded">
                    <Info className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-blue-900">
                        Consider adjusting news detection threshold
                      </p>
                      <p className="text-xs text-blue-700 mt-1">
                        Current threshold is filtering 78% of news content. Lowering to 65% may catch more edge cases.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-green-900">
                        Performance is optimal
                      </p>
                      <p className="text-xs text-green-700 mt-1">
                        Average processing time of 87ms is well below the 250ms target.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}